import React, { useEffect, useState } from 'react'
import {
  DataGrid,
  GridColDef,
  GridCellParams,
  GridToolbar,
  ptBR
} from '@mui/x-data-grid'
import { MdSearch } from 'react-icons/md'
import { toast } from 'react-toastify'
import Tooltip from '@material-ui/core/Tooltip'

import { LicensePlate } from '../../components/LicensePlate'
import { ConfirmRecoverModal } from './ConfirmRecoverModal'
import { useAuth } from '../../contexts/auth'
import api from '../../services/api'

import './styles.scss'

interface Attendance {
  id: number
  status: boolean
  qtyTransported: string
  company: {
    id: number
    name: string
  }
  order: {
    id: number;
    code: string
  }
  driver: {
    name: string
  }
  truck: {
    licensePlate: string
  }
}

const Recuperar = () => {
  const { role } = useAuth()
  const [attendances, setAttendances] = useState<{ data: Attendance[]; isLoading: boolean }>({ data: [], isLoading: false });
  const [attendancesFilter, setAttendancesFilter] = useState<any[]>([])

  const [isOpenModalRecover, setIsOpenModalRecover] = useState(false)
  const [currentVehicleToBeRestored, setCurrentVehicleToBeRestored] =
    useState<Attendance | null>(null)

  const companyId = localStorage.getItem('@App:companyId')
  const isAdmin = role === 'Administrador'

  async function handleConfirmRecover() {
    try {
      await api.put('queue-restore/restore', {
        id: currentVehicleToBeRestored?.id,
        company: {
          id: companyId
        }
      })

      const updatedVehiclesAttendances = attendances.data.filter(
        vehicle => vehicle.id !== currentVehicleToBeRestored?.id
      )

      setAttendances((prev) => ({ ...prev, data: updatedVehiclesAttendances }))

      handleCloseModalRecover()
      toast.success('Aprovação realizada com sucesso')
    } catch (error) {
      toast.error('Erro ao aprovar')
    }
  }

  function handleOpenModalRecover(vehicle: Attendance) {
    setIsOpenModalRecover(true)

    setCurrentVehicleToBeRestored(vehicle)
  }

  function handleCloseModalRecover() {
    setIsOpenModalRecover(false)
    setCurrentVehicleToBeRestored(null)
  }

  function handleChangeSearch(event: React.ChangeEvent<HTMLInputElement>) {
    const search = event.target.value

    const results = attendancesFilter.filter(
      attendance =>
        attendance.truck.licensePlate
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        attendance.driver.name.toLowerCase().includes(search.toLowerCase())
    )
    setAttendances((prev) => ({ ...prev, data: results }))
  }

  const rows = attendances.data.map(item => {
    return {
      id: item.id,
      order: item.order,
      driver: item.driver,
      truck: item.truck,
      status: item.status,
      qtyTransported: item.qtyTransported,
      company: item.company
    }
  })

  const columns: GridColDef<Attendance>[] = [
    { field: 'id', headerName: 'Id', width: 95, hide: true },
    {
      field: 'truck',
      headerName: 'Placa',
      width: 140,
      renderCell: (params: GridCellParams) => {
        return (
          <>
            <LicensePlate
              licensePlate={params.row.truck?.licensePlate}
              themeColor={'#666'}
            />
          </>
        )
      }
    },
    {
      field: 'driver',
      headerName: 'Motorista',
      width: 150,
      renderCell: (params: GridCellParams) => {
        return (
          <>
            <div style={{ lineHeight: 1 }} className='driver'>
              <div>
                <div className='name'>
                  <span>
                    {params.row?.driver?.name
                      .replace(/[de]/g, '')
                      .replace(/[das]/g, '')
                      .split(' ')
                      .slice(0, 2)
                      .join(' ')}
                  </span>
                </div>
                <div className='weight'>
                  <span>{params.row.qtyTransported}</span>
                </div>
              </div>
            </div>
          </>
        )
      }
    },
    {
      field: 'companyName',
      headerName: 'Transportadora',
      width: 150,
      valueGetter: ({ row }) => row.company.name,
      renderCell: ({ row: vehicle }: GridCellParams) => {
        return (
          <p>
            {vehicle.company.name}
          </p>
        )
      }
    },
    {
      field: 'action',
      headerName: 'Ação',
      sortable: false,
      width: 130,
      renderCell: (params: GridCellParams) => {
        const vehicle = params.row

        return (
          <>
            {!vehicle.status && (
              <Tooltip className='aprove' title={<h1>Aprovar Resgate</h1>}>
                <button
                  className='aprove-button'
                  onClick={() => handleOpenModalRecover(vehicle)}
                >
                  Aprovar
                </button>
              </Tooltip>
            )}
          </>
        )
      }
    }
  ]

  useEffect(() => {
    async function getQueueRestore() {
      const route = isAdmin ? "queue-restore" : `queue-restore/?companyId=${companyId}`

      function sortByStatus(vehicle: Attendance) {
        if (vehicle.status) {
          return 1
        }

        if (!vehicle.status) {
          return -1
        }

        return 0
      }

      setAttendances((prev) => ({ ...prev, isLoading: true }))
      try {
        const response = await api.get<Attendance[]>(route)
        const data = response.data.sort(sortByStatus)
        setAttendances((prev) => ({ ...prev, data }))
        setAttendancesFilter(data)
      } finally {
        setAttendances((prev) => ({ ...prev, isLoading: false }))
      }
    }

    getQueueRestore()
  }, [companyId, isAdmin])

  return (
    <div className='productList recover-screen'>
      <header>
        <div>
          <strong>Gerenciar Resgates</strong>
        </div>

        <div className='search-header'>
          <div className='search'>
            <input onChange={handleChangeSearch} placeholder='Buscar Resgate' />
            <MdSearch fontSize={26} color='#999999' />
          </div>
        </div>
      </header>

      <div className='grid'>
        <div className='content'>
          <div style={{ height: 600 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={6}
              rowHeight={70}
              disableColumnMenu
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    companyName: isAdmin,
                  }
                }
              }}
              components={{
                Toolbar: GridToolbar,
                LoadingOverlay: () => (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
                    <p>Carregando...</p>
                  </div>
                )
              }}
              loading={attendances.isLoading}
              rowsPerPageOptions={[8]}
              localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            />
          </div>
        </div>
      </div>

      <ConfirmRecoverModal
        open={isOpenModalRecover}
        onCloseModal={handleCloseModalRecover}
        onConfirmRecover={handleConfirmRecover}
        order={currentVehicleToBeRestored?.order.code}
        licensePlate={currentVehicleToBeRestored?.truck.licensePlate}
        qtyTransported={currentVehicleToBeRestored?.qtyTransported}
      />
    </div>
  )
}

export default Recuperar
